import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  $getTableCellNodeFromLexicalNode,
  $isTableSelection,
  TableCellNode,
} from '@lexical/table';
import {
  $createParagraphNode,
  $getSelection,
  $isRangeSelection,
  COMMAND_PRIORITY_CRITICAL,
  CUT_COMMAND,
  KEY_BACKSPACE_COMMAND,
  KEY_DELETE_COMMAND,
} from 'lexical';
import { useEffect } from 'react';

function $clearTableCellContents(cellNode: TableCellNode): void {
  cellNode.clear();
  cellNode.append($createParagraphNode());
}

export function TableRowClearPlugin() {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    const handleTableCellClear = (): boolean => {
      const selection = $getSelection();

      if ($isTableSelection(selection)) {
        const nodes = selection.getNodes();
        nodes.forEach((node) => {
          const cellNode = $getTableCellNodeFromLexicalNode(node);
          if (cellNode) {
            $clearTableCellContents(cellNode);
          }
        });
        return true;
      }

      if ($isRangeSelection(selection)) {
        const anchor = selection.anchor.getNode();
        const focus = selection.focus.getNode();

        const anchorCell = $getTableCellNodeFromLexicalNode(anchor);
        const focusCell = $getTableCellNodeFromLexicalNode(focus);

        if (anchorCell && focusCell && anchorCell !== focusCell) {
          $clearTableCellContents(anchorCell);
          $clearTableCellContents(focusCell);
          return true;
        }
      }

      return false;
    };

    const unregisterCut = editor.registerCommand(
      CUT_COMMAND,
      () => handleTableCellClear(),
      COMMAND_PRIORITY_CRITICAL
    );

    const unregisterDelete = editor.registerCommand(
      KEY_DELETE_COMMAND,
      () => handleTableCellClear(),
      COMMAND_PRIORITY_CRITICAL
    );

    const unregisterBackspace = editor.registerCommand(
      KEY_BACKSPACE_COMMAND,
      () => handleTableCellClear(),
      COMMAND_PRIORITY_CRITICAL
    );

    return () => {
      unregisterCut();
      unregisterDelete();
      unregisterBackspace();
    };
  }, [editor]);

  return null;
}
